import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { isExperimentApprovalsEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { Experiment } from '@gonfalon/experiments/types';
import { toAIConfigTargeting, toFlagTargeting, toHref } from '@gonfalon/navigator/navigators';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Alert, AlertText, Button, Link } from '@launchpad-ui/components';
import { useSuspenseQuery } from '@tanstack/react-query';

import { useToggleExperimentFlagOn } from '#components/experimentation/common/hooks/useToggleExperimentFlagOn';

import styles from './TurnOnFlagAlert.module.css';

type TurnOnFlagAlertProps = {
  typeString: string;
  experiment: Experiment | undefined;
  flagKey: string;
};

export function TurnOnFlagAlert({ typeString, experiment, flagKey }: TurnOnFlagAlertProps) {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const { data: flagData } = useSuspenseQuery(
    getFeatureFlagQuery({ projectKey, featureFlagKey: flagKey, query: { env: [environmentKey] } }),
  );

  const isAiConfig = flagData._purpose === 'ai';
  const flagOrConfig = isAiConfig ? 'config' : 'flag';
  const isFlagOff = !flagData.environments?.[environmentKey]?.on;
  const canRequestApproval = isExperimentApprovalsEnabled();

  const { toggleOn, isToggling, hasToggledOn } = useToggleExperimentFlagOn({
    projectKey,
    environmentKey,
    flagKey,
    isAiConfig,
    component: 'TurnOnFlagAlert',
    experiment,
  });

  if (hasToggledOn) {
    return (
      <Alert variant="inline" status="success" className={styles.alert}>
        The {flagOrConfig} has been turned on. You can now start the {typeString}.
      </Alert>
    );
  }

  if (!isFlagOff) {
    return null;
  }

  const flagTargetingUrl = toHref(
    isAiConfig
      ? toAIConfigTargeting({ projectKey, configKey: flagKey })
      : toFlagTargeting({ projectKey, flagKey, environmentKey }),
  );

  return (
    <Alert status="info" className={styles.alert}>
      <AlertText className={styles.text}>
        <strong>
          The {typeString}'s {flagOrConfig} is off
        </strong>
        <span>
          Toggle on the <Link href={flagTargetingUrl}>{flagData.name}</Link> {flagOrConfig} to start the {typeString}
          {canRequestApproval ? ', or cancel to request approval first' : ''}.
        </span>
      </AlertText>
      <Button variant="default" onPress={toggleOn} isPending={isToggling} isDisabled={isToggling}>
        Toggle on {flagOrConfig}
      </Button>
    </Alert>
  );
}
